import { Routes } from '@angular/router';
import { HomeComponent } from './components/home/home';
import { DashboardComponent } from './components/dashboard/dashboard';
import { LoginComponent } from './components/login/login';
import { SubmitLinksComponent } from './components/submit-links/submit-links';
import { HistoryComponent } from './components/history/history';
import { CreditsComponent } from './components/credits/credits';
import { FaqComponent } from './components/faq/faq';
import { HelpComponent } from './components/help/help';
import { DripGuideComponent } from './components/drip-guide/drip-guide.component';
import { PrivacyPolicyComponent } from './components/privacy-policy/privacy-policy.component';
import { TermsConditionsComponent } from './components/terms-conditions/terms-conditions.component';
import { RefundsPolicyComponent } from './components/refunds-policy/refunds-policy.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { PaymentComponent } from './components/payment/payment.component';
import { BlogHomeComponent } from './components/blog/blog-home/blog-home.component';
import { BlogPostComponent } from './components/blog/blog-post/blog-post.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { AuthGuard } from './guards/auth.guard';

const OG_IMAGE = 'https://quickindexingtool.com/og-image.png';
const BRAND = 'Quick Indexing Tool';

export const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    data: {
      title: `${BRAND} — Get Your URLs Indexed in Minutes`,
      description: 'Submit URLs and get your pages indexed by Google fast. 98.5% index rate, real-time tracking, developer API, and drip mode scheduling.',
      keywords: 'url indexer, backlink indexer, fast google indexing, instant indexing, seo tool, quick indexing',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  { path: 'home', redirectTo: '', pathMatch: 'full' },
  {
    path: 'login',
    component: LoginComponent,
    data: {
      title: `Sign In — ${BRAND}`,
      description: 'Sign in to your account to submit URLs, track indexing progress, and manage your credits.',
      keywords: 'login, sign in, indexing dashboard',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
    data: {
      title: `Dashboard — ${BRAND}`,
      description: 'Monitor your URL submissions, track indexing progress, and manage your crawling pipeline.',
      robots: 'noindex, nofollow'
    }
  },
  {
    path: 'submit',
    component: SubmitLinksComponent,
    canActivate: [AuthGuard],
    data: {
      title: `Submit URLs — ${BRAND}`,
      description: 'Submit your URLs for fast Google indexing with real-time tracking.',
      robots: 'noindex, nofollow'
    }
  },
  { path: 'submit-links', redirectTo: 'submit', pathMatch: 'full' },
  {
    path: 'history',
    component: HistoryComponent,
    canActivate: [AuthGuard],
    data: {
      title: `Submission History — ${BRAND}`,
      description: 'View your URL submission history, indexing status, and crawl logs.',
      robots: 'noindex, nofollow'
    }
  },
  { path: 'credits', redirectTo: 'pricing', pathMatch: 'full' },
  {
    path: 'pricing',
    component: CreditsComponent,
    data: {
      title: `Pricing — URL Indexing Credits from $15 | ${BRAND}`,
      description: 'Buy URL indexing credits starting at $15. No subscription — pay as you go. Each credit indexes one URL. Pay with 200+ cryptocurrencies.',
      keywords: 'indexing credits pricing, url indexer pricing, backlink indexer cost',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'drip-guide',
    component: DripGuideComponent,
    data: {
      title: `Drip Mode Guide — Natural URL Scheduling | ${BRAND}`,
      description: 'Learn how to use Drip Mode to schedule URL submissions over time for natural indexing patterns.',
      keywords: 'drip indexing, scheduled url submission, natural indexing',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'faq',
    component: FaqComponent,
    data: {
      title: `FAQ — How URL Indexing Works | ${BRAND}`,
      description: 'Get answers about URL indexing, credits, indexing speed, API access, and how we help your SEO.',
      keywords: 'url indexing FAQ, indexer questions, how indexing works',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'help',
    component: HelpComponent,
    data: {
      title: `Help Center — ${BRAND}`,
      description: 'Setup guides, API documentation, troubleshooting, and support resources.',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent,
    data: {
      title: `Privacy Policy — ${BRAND}`,
      description: 'How we collect, use, and protect your data.',
      robots: 'index, follow'
    }
  },
  {
    path: 'terms-conditions',
    component: TermsConditionsComponent,
    data: {
      title: `Terms of Service — ${BRAND}`,
      description: 'Terms and conditions for using our indexing services.',
      robots: 'index, follow'
    }
  },
  {
    path: 'refunds-policy',
    component: RefundsPolicyComponent,
    data: {
      title: `Refund Policy — ${BRAND}`,
      description: 'Our refund policy for credit purchases.',
      robots: 'index, follow'
    }
  },
  {
    path: 'contact-us',
    component: ContactUsComponent,
    data: {
      title: `Contact Us — ${BRAND}`,
      description: 'Get in touch with our support team.',
      keywords: 'contact support, indexing tool help',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'payment',
    component: PaymentComponent,
    data: {
      title: `Payment — ${BRAND}`,
      description: 'Complete your credit purchase securely.',
      robots: 'noindex, nofollow'
    }
  },

  // Blog Routes
  {
    path: 'blog',
    component: BlogHomeComponent,
    data: {
      title: `SEO & Indexing Blog — Tips & Guides | ${BRAND}`,
      description: 'Learn how to get pages indexed faster, fix indexing issues, and boost your SEO with expert guides.',
      keywords: 'SEO blog, google indexing tips, indexing guide',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },
  {
    path: 'blog/:slug',
    component: BlogPostComponent,
    data: {
      title: `Blog — ${BRAND}`,
      description: 'Expert guides on URL indexing, SEO strategies, and search optimization.',
      robots: 'index, follow',
      ogImage: OG_IMAGE
    }
  },


  {
    path: 'api-docs',
    loadComponent: () => import('./components/api-docs/api-docs').then(m => m.ApiDocsComponent),
    data: {
      title: `Developer API Documentation — ${BRAND}`,
      description: 'Automate your indexing pipeline with our REST API.',
      robots: 'index, follow'
    }
  },
  // 404
  {
    path: '**',
    component: NotFoundComponent,
    data: {
      title: `Page Not Found — ${BRAND}`,
      description: 'The page you are looking for does not exist.',
      robots: 'noindex, nofollow'
    }
  }
];
